import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import { formatNumber } from "utils/string";

import BrandBody from "../../../Discover/DetailedBrandView/components/BrandBody";
import { useBrandDetails } from "../../../Discover/DetailedBrandView/components/BrandBody/useBrandDetails";
import BrandMetadata from "../../../Discover/DetailedBrandView/components/BrandMetadata";
import { OutreachAutomationBrand } from "../../types";
import BrandPreviewDialog from "../BrandPreviewDialog";

interface BrandReviewCardProps {
  brand: OutreachAutomationBrand;
  variant: "preview" | "full";
  onApprove: (brand: OutreachAutomationBrand) => void;
  onReject: (brand: OutreachAutomationBrand) => void;
  isUpdating: boolean;
}

function formatFoundTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diffDays = Math.floor((now - timestamp) / 86400);
  if (diffDays === 0) return "Found today";
  if (diffDays === 1) return "Found 1 day ago";
  return `Found ${diffDays} days ago`;
}

function formatQueuedTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diffDays = Math.floor((now - timestamp) / 86400);
  if (diffDays === 0) return "Found today";
  if (diffDays === 1) return "Found 1 day ago";
  if (diffDays < 30) return `Found ${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "Found about 1 month ago";
  return `Found about ${diffMonths} months ago`;
}

export default function BrandReviewCard({
  brand,
  variant,
  onApprove,
  onReject,
  isUpdating,
}: BrandReviewCardProps) {
  const { bentoBrand, outreachAutomation } = brand;
  const displayName = bentoBrand.normalizedName || "Unknown Brand";
  const isApprovalRequired =
    outreachAutomation.approvalMode === "approval_required";
  const isPreview = variant === "preview";

  const [expanded, setExpanded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const {
    fetchSelectedBentobrand,
    selectedBrand,
    fetchLoading,
    similarBrands,
  } = useBrandDetails({ brandDetailsId: bentoBrand.id });

  useEffect(() => {
    if (bentoBrand.id && expanded) {
      fetchSelectedBentobrand(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bentoBrand.id, expanded]);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <Box
      onClick={() => {
        if (!previewOpen) setPreviewOpen(true);
      }}
      sx={{
        backgroundColor: "white",
        border: "1px solid rgba(1,67,57,0.1)",
        borderRadius: "12px",
        p: 2,
        opacity: isUpdating ? 0.5 : 1,
        pointerEvents: isUpdating ? "none" : "auto",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Left: Avatar + Info */}
        <Box sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar
            src={bentoBrand.logoUrl}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: bentoBrand.backgroundLogoColor || "#e5e7eb",
              fontSize: 16,
              fontWeight: 600,
              color: "#014339",
              flexShrink: 0,
            }}
          >
            {displayName[0]?.toUpperCase()}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                lineHeight: "22px",
                color: "#014339",
              }}
            >
              {displayName}
            </Typography>

            {/* Instagram username + follower count + found time */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mt: 0.25,
              }}
            >
              {bentoBrand.instagramUsername && (
                <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                  @{bentoBrand.instagramUsername}
                </Typography>
              )}
              {bentoBrand.instagramFollowerCount != null && (
                <>
                  {bentoBrand.instagramUsername && (
                    <Typography
                      sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}
                    >
                      &bull;
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                    {formatNumber(bentoBrand.instagramFollowerCount)} followers
                  </Typography>
                </>
              )}
              {(bentoBrand.instagramUsername ||
                bentoBrand.instagramFollowerCount != null) && (
                <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                  &bull;
                </Typography>
              )}
              <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                {isPreview
                  ? formatFoundTime(brand.createdAt)
                  : formatQueuedTime(brand.createdAt)}
              </Typography>
            </Box>

            {/* Links + More/Less */}
            <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
              {bentoBrand.website && (
                <LinkChip
                  href={`https://${bentoBrand.website}`}
                  icon="fa-solid fa-globe"
                  label="Website"
                />
              )}
              {bentoBrand.instagramUsername && (
                <LinkChip
                  href={`https://instagram.com/${bentoBrand.instagramUsername}`}
                  icon="fa-brands fa-instagram"
                  label="Instagram"
                />
              )}
              <Box
                onClick={handleToggleExpand}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  border: "1px solid rgba(1,67,57,0.2)",
                  borderRadius: "4px",
                  px: "9px",
                  py: "1px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(1,67,57,0.03)" },
                }}
              >
                <Box
                  component="span"
                  className={
                    expanded
                      ? "fa-solid fa-chevron-up"
                      : "fa-solid fa-chevron-down"
                  }
                  sx={{ fontSize: 8, color: "#014339" }}
                />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#014339",
                    lineHeight: "15px",
                  }}
                >
                  {expanded ? "Less" : "More"}
                </Typography>
              </Box>
            </Box>

            {/* Source automation (preview only) */}
            {isPreview && (
              <Typography
                sx={{
                  fontSize: 10,
                  lineHeight: "15px",
                  fontWeight: 500,
                  color: "#b1b3f8",
                  mt: 1,
                }}
              >
                from: {outreachAutomation.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {isUpdating ? (
            <CircularProgress size={20} sx={{ color: "primary.main" }} />
          ) : isApprovalRequired ? (
            <>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewOpen(true);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "8px",
                  px: 1.5,
                  height: 28,
                  border: "1px solid rgba(1,67,57,0.15)",
                  backgroundColor: "rgba(1,67,57,0.06)",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(1,67,57,0.1)" },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-eye"
                  sx={{ fontSize: 10, color: "#014339" }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#014339",
                    lineHeight: "16px",
                  }}
                >
                  Preview
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(brand);
                }}
                sx={{
                  backgroundColor: "#014339",
                  color: "white",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 12,
                  px: 1.5,
                  height: 28,
                  minWidth: 0,
                  lineHeight: "16px",
                  "&:hover": { backgroundColor: "#012e27" },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-check"
                  sx={{ fontSize: 10, mr: 0.5 }}
                />
                Approve
              </Button>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(brand);
                }}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #ffc9c9",
                  "&:hover": { backgroundColor: "#fee2e2" },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-trash-can"
                  sx={{ fontSize: 11, color: "#dc2626" }}
                />
              </IconButton>
            </>
          ) : (
            <>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewOpen(true);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "8px",
                  px: 1.5,
                  height: 28,
                  border: "1px solid rgba(1,67,57,0.15)",
                  backgroundColor: "rgba(1,67,57,0.06)",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(1,67,57,0.1)" },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-eye"
                  sx={{ fontSize: 10, color: "#014339" }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#014339",
                    lineHeight: "16px",
                  }}
                >
                  Preview
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#dcfce7",
                  border: "1px solid #7bf1a8",
                  borderRadius: "100px",
                  px: 1,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-clock"
                  sx={{ fontSize: 10, color: "#008236" }}
                />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#008236",
                    lineHeight: "16px",
                  }}
                >
                  Scheduled
                </Typography>
              </Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(brand);
                }}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #ffc9c9",
                  "&:hover": { backgroundColor: "#fee2e2" },
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-trash-can"
                  sx={{ fontSize: 11, color: "#dc2626" }}
                />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Expanded brand details */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 2, borderTop: "1px solid rgba(1,67,57,0.1)", pt: 2 }}>
          <Box display="flex" flexDirection="row" gap={0.5} sx={{ mb: 1 }}>
            <BrandMetadata
              selectedBrand={selectedBrand}
              noGrid
              clickable={false}
            />
          </Box>

          {bentoBrand.aboutSummary && (
            <Typography color="textSecondary" sx={{ fontSize: 12, mb: 1 }}>
              {bentoBrand.aboutSummary}
            </Typography>
          )}

          <BrandBody
            selectedBrand={selectedBrand}
            fetchLoading={fetchLoading}
            similarBrands={similarBrands}
            fetchSimilarLoading={false}
            source={BrandCardSource.MORE_INFO_BRAND}
          />
        </Box>
      </Collapse>

      <BrandPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        brand={brand}
      />
    </Box>
  );
}

// ─── Shared mini link chip ───────────────────────────────────────────────────

function LinkChip({
  href,
  icon,
  label,
}: {
  href: string;
  icon: string;
  label: string;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          border: "1px solid rgba(1,67,57,0.2)",
          borderRadius: "4px",
          px: "9px",
          py: "1px",
          backgroundColor: "white",
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(1,67,57,0.03)" },
        }}
      >
        <Box
          component="span"
          className={icon}
          sx={{ fontSize: 10, color: "#014339" }}
        />
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 500,
            color: "#014339",
            lineHeight: "15px",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Link>
  );
}
